/**
 * Authentication Error Boundary Component
 * 
 * Specialized error boundary for authentication components that:
 * - Catches errors in authentication flow (MsalRedirectHandler, etc.)
 * - Provides auth-specific error recovery
 * - Logs errors to telemetry
 * - Redirects to login on critical auth errors
 * 
 * This is a recommendation from CODE_REVIEW_CONTEXT7.md (Section 7.3)
 */

import { Component, ErrorInfo, ReactNode } from 'react';
import { logStructuredAuthEvent } from '../../utils/authTelemetry.utils';
import './AuthErrorBoundary.css';

interface AuthErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface AuthErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Authentication Error Boundary
 * Catches errors in authentication components and provides recovery options
 */
export class AuthErrorBoundary extends Component<AuthErrorBoundaryProps, AuthErrorBoundaryState> {
  constructor(props: AuthErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<AuthErrorBoundaryState> {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Update state with error info
    this.setState({
      error,
      errorInfo,
    });

    // Log error to telemetry with correlation ID
    logStructuredAuthEvent('redirect_processing', {
      outcome: 'failure',
      errorKind: 'error_boundary',
      errorType: error.name || 'UnknownError',
      metadata: {
        errorMessage: error.message,
        componentStack: errorInfo.componentStack,
        errorStack: error.stack,
      },
    });

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Log to console in development
    if (import.meta.env.DEV) {
      console.error('[AuthErrorBoundary] Caught authentication error:', error, errorInfo);
    }
  }

  handleReset = (): void => {
    // Reset error boundary state
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleReload = (): void => {
    // Reload the entire page
    window.location.reload();
  };

  handleGoToLogin = (): void => {
    // Navigate to login page
    // Note: We can't use useNavigate hook in class component, so we use window.location
    window.location.href = '/login';
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // If custom fallback is provided, use it
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Otherwise, render auth-specific error UI
      const error = this.state.error || new Error('An unknown authentication error occurred');
      const isDevelopment = import.meta.env.DEV;

      return (
        <div className="auth-error-boundary-container" role="alert" aria-live="polite">
          <div className="auth-error-boundary-content">
            <div className="auth-error-boundary-icon" aria-hidden="true">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <h1 className="auth-error-boundary-title">Authentication Error</h1>
            <p className="auth-error-boundary-message">
              We encountered an error during the authentication process. This may be a temporary issue.
              Please try signing in again or contact support if the problem persists.
            </p>
            
            {isDevelopment && error && (
              <details className="auth-error-boundary-details">
                <summary className="auth-error-boundary-details-summary">
                  Error Details (Development Only)
                </summary>
                <div className="auth-error-boundary-details-content">
                  <pre className="auth-error-boundary-stack">
                    <strong>Error:</strong> {error.toString()}
                    {this.state.errorInfo && (
                      <>
                        {'\n\n'}
                        <strong>Component Stack:</strong>
                        {'\n'}
                        {this.state.errorInfo.componentStack}
                      </>
                    )}
                  </pre>
                </div>
              </details>
            )}

            <div className="auth-error-boundary-actions">
              <button
                onClick={this.handleGoToLogin}
                className="auth-error-boundary-button auth-error-boundary-button-primary"
                aria-label="Go to login page"
              >
                Go to Login
              </button>
              <button
                onClick={this.handleReset}
                className="auth-error-boundary-button auth-error-boundary-button-secondary"
                aria-label="Try to recover from error"
              >
                Try Again
              </button>
              <button
                onClick={this.handleReload}
                className="auth-error-boundary-button auth-error-boundary-button-tertiary"
                aria-label="Reload the page"
              >
                Reload Page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
